import { useState } from "react";
import { NumberCard } from "../../../components/cards/numberCard";
import Dropdown from "../../../components/dropdown/dropdown";
import { Icon } from "../../../components/icon/icon";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useAdminGetRevenueStatisticsQuery,
  useAdminGetRevenueTableQuery,
} from "../../../redux/features/admin/payments/payments.api";
import { PayoutCurrency } from "../../../redux/features/payment/payment.types";
import { currencyShorts } from "../../../utils/constants";
import { FrequencyFilter, QueryParams } from "../../../utils/types";
import { RevenueTable } from "../../../components/revenue/revenueTable";
import { RevenueStatsChart } from "../../../components/revenue/revenueStatsChart";
import { BackButton } from "../../../components/buttons/backButton";


export const RevenuesPage = () => {
  const [frequency, setFrequency] = useState<FrequencyFilter>("daily");
  const [revenueCurrency, setRevenueCurrency] = useState<PayoutCurrency>("NGN");
  const dropDownOptions = [
    {
      label: "NGN Revenue",
      value: "NGN",
      onClick: () => {
        setRevenueCurrency("NGN");
      },
    },
    {
      label: "CAD Revenue",
      value: "Canadian Dollars",
      onClick: () => {
        setRevenueCurrency("Canadian Dollars");
      },
    },
  ];

  const [query, setQuery] = useState<QueryParams>({
    page: 1,
    limit: 20,
  });

  const { isFetching: fetchingRevenueStats, data: revenueStats } =
    useAdminGetRevenueStatisticsQuery(
      {
        filter: frequency,
        currency: currencyShorts[revenueCurrency] as PayoutCurrency,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const { isFetching: fetchingRevenueData, data: revenueData } =
    useAdminGetRevenueTableQuery(
      { ...query },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const totalAmountProcessed =
    Number(revenueStats?.data?.totalPaidOut ?? 0) +
    Number(revenueStats?.data?.totalRevenue ?? 0);

  return (
    <div className="w-full">
      <BackButton />
      <div className="flex items-center gap-1">
        <PageHeader
          header={`${currencyShorts[revenueCurrency]} Revenue`}
          className="!font-dela font-normal"
        />
        <Dropdown
          menuItems={dropDownOptions.map((option) => {
            return {
              name: (
                <p className="soft-shrink flex items-center gap-2 !rounded-md !bg-white p-3 text-left !font-bold">
                  {option.label}
                </p>
              ),
              onClick: option.onClick,
            };
          })}
          className=""
          menuClassName="!gap-1 !border-0 !bg-grey-50 !p-1 !w-[200px] !z-[998]"
          menuItemsClassName="border-none !text-base !bg-transparent !p-0"
          position="bottom end"
        >
          <p className="soft-shrink flex items-center gap-2 !rounded-[800px] !bg-light-grey px-3 py-3 text-left !font-bold">
            <Icon
              svg={"caret-down"}
              width={20}
              height={20}
              className={`text-grey-400} !min-w-fit`}
            />
          </p>
        </Dropdown>
      </div>

      <div className="mt-8 flex flex-col gap-[2px]">
        <BgWrapper className="w-full">
          <RevenueStatsChart
            fetchingRevenueStats={fetchingRevenueStats}
            revenueStats={revenueStats}
            frequency={frequency}
            setFrequency={setFrequency}
            currency={revenueCurrency}
          />
        </BgWrapper>
        <BgWrapper className="grid grid-cols-1 gap-[10px] md:grid-cols-2 lg:grid-cols-4">
          <NumberCard
            value={totalAmountProcessed ?? 0}
            text="Total Amount Processed"
            isCurrency
            loading={fetchingRevenueStats}
            titleFirst
            decimalPlaces={2}
          />
          <NumberCard
            value={Number(revenueStats?.data?.totalPaidOut ?? 0)}
            text="Total Amount Paid"
            isCurrency
            loading={fetchingRevenueStats}
            titleFirst
            decimalPlaces={2}
          />

          <NumberCard
            value={Number(revenueStats?.data?.totalRevenue ?? 0)}
            text="Total Tikket Revenue"
            isCurrency
            loading={fetchingRevenueStats}
            titleFirst
            decimalPlaces={2}
          />
          <NumberCard
            value={revenueStats?.data?.totalTicketsSold ?? 0}
            text="Total Paid Tickets Sold"
            isCurrency={false}
            loading={fetchingRevenueStats}
            titleFirst
            decimalPlaces={2}
          />
        </BgWrapper>
        <BgWrapper className="flex flex-col gap-3">
          <MiniHeader header="Revenue MoM" className="text-primary" />
          <RevenueTable
            fetchingRevenueData={fetchingRevenueData}
            revenueData={revenueData}
            query={query}
            setQuery={setQuery}
            clickable
          />
        </BgWrapper>
      </div>
    </div>
  );
};
